/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * Page SubscriberId init translation
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Andrey Dubov
 * @author Ilya Baranchikov
 * @author Nikolay Sviridov
 * @version 1.34.0
 * @since 1.12.0
 */

import { IPageSubscriberIdTranslation } from '../../types/pages/subscriberId';

const initTranslationPageSubscriberId: IPageSubscriberIdTranslation = {
    pageTitle: {
        roamText: '',
        inRoamingBadgeText: '',
    },
    error: {
        btn: '',
        title: '',
        tooltip: '',
    },
    sideMenu: {
        additionalAccounts: '',
        dcRule: '',
        finances: '',
        info: '',
        manageNumber: '',
        personalData: '',
        products: '',
    },
    status: {
        common: '',
        lock: '',
        lockTooltip: '',
    },
    tabs: {
        additionalAccounts: {
            balance: '',
            btnConnection: '',
            btnDisconnection: '',
            contentAccountConnectionTooltip: '',
            contentAccountTitle: '',
            date: '',
            info: '',
            number: '',
            personalAccountDisconnectionTooltip: '',
            personalAccountTitle: '',
            status: '',
        },
        dcRules: {
            empty: '',
            notification: {
                notifyOverBreak: '',
                notifyOverWarning: '',
                notifySetOrChangeLimits: '',
                notifyStartUseLimits: '',
                title: '',
            },
            rules: {
                break: '',
                measure: '',
                name: '',
                noValue: '',
                warn: '',
            },
            rulesList: {
                dateStart: '',
                dateStartAlready: '',
                period: {
                    dateChange: '',
                    dateEnd: '',
                    dateStart: '',
                    dateStartAlready: '',
                },
                showShort: '',
                showAll: '',
                title: '',
            },
            schedule: {
                noTime: '',
                subTitle: '',
                title: '',
            },
        },
        finances: {
            preliminaryData: '',
            preliminaryDataLink: '',
            btnCallDetails: '',
            dataIncomplete: '',
            empty: '',
            total: {
                date: '',
                title: '',
            },
        },
        info: {
            accountTitle: '',
            accessPoints: '',
            campaigns: {
                title: '',
                value: '',
            },
            piggyBank: {
                range: '',
                rangeTitle: '',
                title: '',
            },
            regionTitle: '',
            status: {
                btn: {
                    blocked: '',
                    unblocked: '',
                },
                title: '',
            },
            statusPersonalData: {
                title: '',
            },
            tariff: {
                btn: '',
                datePlan: '',
                datePlanTooltip: '',
                title: '',
            },
            megaPowers: {
                btn: '',
                title: '',
            },
            tariffDiscounts: {
                unlimited: '',
                priceError: '',
                priceFree: '',
                rangeWithUnit: '',
                rangeWithTwoUnit: '',
                showAll: '',
                showShort: '',
                tariffOptions: '',
                title: '',
                otherTitle: '',
                dateToText: '',
            },
            userTypeTitle: '',
            units: {
                piece: '',
                minute: '',
                byte: '',
                kilobyte: '',
                megabyte: '',
                gigabyte: '',
                terabyte: '',
            },
        },
        manageNumber: {
            position: {
                create: {
                    btn: '',
                    open: '',
                    placeholder: '',
                },
                select: {
                    empty: '',
                    placeholder: '',
                },
                title: '',
            },
            labels: {
                change: '',
                empty: '',
                showAll: '',
                showShort: '',
                title: '',
            },
            redirection: {
                add: {
                    description: '',
                    fields: {
                        phone: {
                            label: '',
                        },
                        type: {
                            label: '',
                            options: {
                                BUSY: '',
                                NO_ANSWER: '',
                                NON_AVAILABILITY: '',
                                UNCONDITIONAL: '',
                            },
                            placeholder: '',
                        },
                        time: {
                            label: '',
                            placeholder: '',
                        },
                    },
                    title: '',
                },
                empty: '',
                error: '',
                remove: {
                    failure: {
                        text: '',
                        title: '',
                    },
                    start: {
                        text: '',
                        title: '',
                    },
                },
                table: {
                    btnAdd: '',
                    description: {
                        BUSY: '',
                        NO_ANSWER: '',
                        NON_AVAILABILITY: '',
                        UNCONDITIONAL: '',
                    },
                    status: {
                        BUSY: '',
                        NO_ANSWER: '',
                        NON_AVAILABILITY: '',
                        UNCONDITIONAL: '',
                    },
                    title: '',
                },
            },
            simCard: {
                btn: {
                    add: '',
                    change: '',
                },
                empty: '',
                iccid: '',
                modal: {
                    placeholder: '',
                    subTitle: '',
                    suggestSubTitle: '',
                    title: {
                        add: '',
                        change: '',
                    },
                },
                noFoundItems: '',
                puk1: '',
                puk2: '',
                title: '',
            },
        },
        personalData: {
            address: {
                fields: {},
                title: '',
            },
            btn: {
                add: '',
                confirm: '',
                delete: '',
                edit: '',
            },
            deleteModal: {
                failure: {
                    title: '',
                },
                start: {
                    subtitle: '',
                    text: '',
                    title: '',
                },
                success: {
                    title: '',
                },
            },
            document: {
                fields: {
                    type: '',
                },
                title: '',
            },
            files: {
                fields: { id: '', name: '' },
                title: '',
            },
            personalData: {
                fields: {
                    birthDate: '',
                },
                title: '',
            },
            personalDataEdit: {
                requiredMessage: '',
                fields: {
                    birthDate: '',
                    name: '',
                    patronymic: '',
                    patronymicIsOptional: '',
                    surname: '',
                },
                title: '',
                file: {
                    formats: '',
                    maxSize: '',
                },
                modal: {
                    start: {
                        saveData: '',
                    },
                    failure: {
                        confirm: '',
                        email: '',
                        remove: '',
                        saveData: '',
                        upload: '',
                    },
                    success: {
                        confirm: '',
                        saveData: '',
                    },
                },
                warning: {
                    notPassport: '',
                    personalDataConfirmation: '',
                },
            },
            personalDataEmpty: '',
            status: {
                title: '',
            },
            tooltip: {},
        },
        products: {
            btnConnection: '',
            date: '',
            datePlanConnected: '',
            datePlanDisconnected: '',
            datePlanShowMore: '',
            descriptionLink: '',
            empty: '',
            price: '',
            priceFree: '',
            personalAccount: '',
        },
    },
};

export default initTranslationPageSubscriberId;
